import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import { Line, LineChart, ResponsiveContainer, YAxis } from 'recharts';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { StatValue } from './StatValue';

import * as adminApi from '../api/admin';
import type { AiSpendSummary } from '../api/admin';

/**
 * Platform-dashboard AI-spend card (DASH-AI-SPEND).
 *
 * Headline is the rolling 30-day total. Subtext breaks down by
 * provider — OpenAI (Whisper + the chat model); rows from before
 * 2026-10 may also say OpenRouter, the old chat provider. Below: a
 * tiny no-chrome sparkline of per-day spend so
 * a sudden ramp jumps out at a glance.
 *
 * Polls every 60 s — usage data ticks at the marker_labels job
 * cadence (every few minutes at most), so a longer cadence keeps
 * the network noise down without losing accuracy.
 */

const POLL_INTERVAL_MS = 60_000;

export function AiSpendCard({
  size = { xs: 12, md: 6 },
}: {
  /** Grid column span. Defaults to half-width (md: 6). */
  size?: React.ComponentProps<typeof Grid>['size'];
} = {}) {
  const [summary, setSummary] = useState<AiSpendSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await adminApi.getAiSpend('month');
        if (!cancelled) {
          setSummary(next);
          setError(null);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(
            (err as { response?: { data?: { detail?: string } } })?.response
              ?.data?.detail ?? 'failed to load AI spend',
          );
        }
      }
    };
    void tick();
    const handle = setInterval(() => void tick(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, []);

  return (
    <Grid size={size} sx={{ display: 'flex' }}>
      <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <AutoAwesomeIcon color="primary" fontSize="small" />
            <Typography variant="overline" color="text.secondary">
              AI spend (30 days)
            </Typography>
          </Stack>
          {summary === null && error === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : summary !== null ? (
            <SpendBody summary={summary} />
          ) : null}
        </CardContent>
      </Card>
    </Grid>
  );
}

function SpendBody({ summary }: { summary: AiSpendSummary }) {
  const fmt = (n: number) => `$${n.toFixed(2)}`;
  const providerBreakdown = summary.by_provider
    .map((p) => `${p.provider} ${fmt(p.cost_usd)}`)
    .join(' · ');
  return (
    <>
      <StatValue value={fmt(summary.total_cost_usd)} />
      <Typography variant="body2" color="text.secondary">
        {summary.request_count} call{summary.request_count === 1 ? '' : 's'}
        {providerBreakdown ? ` · ${providerBreakdown}` : ''}
      </Typography>
      {/* Tiny sparkline — no axes, no tooltip, no dots. Just the
          shape of the trend, so the operator sees a daily ramp
          before it surprises them in a monthly bill. `flex: 1` +
          `minHeight` lets the chart fill whatever vertical space
          is left after the headline + subtext, so the card matches
          the height of its taller sibling (PipelineStatusCard's
          worker chip strip) without leaving an empty band. */}
      <Box sx={{ flex: 1, minHeight: 48, mt: 1.5 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={summary.by_day}
            margin={{ top: 4, right: 0, left: 0, bottom: 0 }}
          >
            <YAxis hide domain={[0, 'auto']} />
            <Line
              type="monotone"
              dataKey="cost_usd"
              stroke="#1e3a8a"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </>
  );
}
